/*
    The platform's filter chip: a labelled button that opens a checklist.

    Built for the Engagements list (MPN-1720) and shared from here since the Holdout Test
    Results report adopted the same filters (MPN-2045, Holly's updated prototype). One
    component rather than two, because a second one would drift from this the first time
    either page gained a behaviour -- and this one carries two fixes that are easy to lose:

      - The panel is PORTALLED to document.body and positioned `fixed`. Both pages put their
        filters inside a card that clips its own overflow, and an absolutely positioned panel
        is clipped by that card however much viewport room it measured. On a short result set
        it was sliced mid-row with the Clear link gone.
      - Placement is MEASURED, so a chip near the bottom of the page opens upward instead of
        off the end of the viewport.

    Styles live beside it in chipMenu.scss. The `eng-` prefix is historical and deliberately
    kept: renaming it would be a rename across two pages and their tests for nothing.
*/
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import "./chipMenu.scss";

export interface MenuOption {
  value: string;
  label: string;
}

/** Add or remove one value. Exported because every caller holds its own filter state. */
export function toggleValue(current: string[], value: string): string[] {
  return current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
}

/** Panel sizing. A floor as well as a ceiling, so a cramped viewport still shows a usable
    scrolling list rather than a two-row sliver. */
const PANEL_MAX = 280;
const PANEL_MIN = 140;
const PANEL_GAP = 6;
const VIEWPORT_MARGIN = 12;

/** Where the portalled panel sits, in viewport coordinates. */
interface Placement {
  up: boolean;
  maxHeight: number;
  left: number;
  top: number;
  minWidth: number;
}

/*
    A dropdown of checkbox rows, or of radio rows when `single` is set.

    One component for both because they differ only in the glyph and in whether picking
    closes the menu; two would drift the moment one gained a behaviour.
*/
export default function ChipMenu({
  label,
  options,
  selected,
  onToggle,
  onClear,
  single = false,
}: {
  label: string;
  options: MenuOption[];
  selected: string[];
  onToggle: (value: string) => void;
  onClear?: () => void;
  single?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  /*
      Where the panel fits, measured rather than assumed.

      It used to always drop downward at a fixed max height, so a filter opened near the
      bottom of the page was clipped by the viewport: the last few options and the Clear link
      simply were not there. The CSM list showed it most, being the longest, but any list hit
      it at the right scroll position.

      So: measure the space above and below the chip, open into whichever is larger, and cap
      the panel to it. Remeasured on scroll and resize, because the chip moves under it.
  */
  const [placement, setPlacement] = useState<Placement>(
    { up: false, maxHeight: PANEL_MAX, left: 0, top: 0, minWidth: 0 },
  );
  /*
      The panel is rendered into document.body, not beside the chip.

      Measuring against the viewport is necessary and was not sufficient: `.eng-list` is
      `overflow: hidden` to keep rows inside its 10px radius, so an absolutely positioned
      panel is clipped by the CARD however much viewport room it measured. On a long list
      nobody noticed, because the card was taller than the panel. On an empty result it cut
      the panel off mid-row with half the page empty below it, and Clear went with it
      (Holly, QA, 6 Oct 2026).

      A portal takes the panel out of every ancestor's overflow at once, so this cannot come
      back via some other container growing a clip. Position is fixed and recomputed on
      scroll and resize, which is what `position: fixed` costs.
  */
  const popRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;

    const measure = () => {
      const chip = ref.current?.firstElementChild;
      if (!chip) return;
      const rect = chip.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - PANEL_GAP - VIEWPORT_MARGIN;
      const above = rect.top - PANEL_GAP - VIEWPORT_MARGIN;
      // Downward unless there is meaningfully more room the other way. Flipping over a few
      // pixels would make the panel jump about as the page scrolls.
      const up = below < PANEL_MIN && above > below;
      setPlacement({
        up,
        maxHeight: Math.max(PANEL_MIN, Math.min(PANEL_MAX, up ? above : below)),
        // Viewport coordinates, because the panel is fixed. Left-aligned to the chip, then
        // pulled back if that would push it off the right edge.
        left: Math.max(
          VIEWPORT_MARGIN,
          Math.min(rect.left, window.innerWidth - rect.width - VIEWPORT_MARGIN),
        ),
        top: up ? rect.top - PANEL_GAP : rect.bottom + PANEL_GAP,
        minWidth: rect.width,
      });
    };

    measure();
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      // Both, now that the panel is not a descendant of the chip: without the second test
      // every click inside the menu would close it before the option registered.
      if (ref.current?.contains(target) || popRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open]);

  /*
      Open with the current choice in view (Holly, 6 Oct 2026). A list of twelve capped at
      280px scrolls, and a panel that opens at the top while the ticked row sits below the
      fold reads as nothing being selected at all.
  */
  useEffect(() => {
    if (!open) return;
    const checked = popRef.current?.querySelector("[data-checked='true']");
    checked?.scrollIntoView({ block: "nearest" });
  }, [open]);

  const labelFor = (value: string) =>
    options.find((option) => option.value === value)?.label ?? value;

  // A single-select chip always shows its current choice, because there is always one.
  // A multi-select chip reads "any" until something is picked.
  const active = selected.length > 0;
  const text = single
    ? `${label}: ${labelFor(selected[0])}`
    : !active
      ? `${label}: any`
      : selected.length === 1
        ? `${label}: ${labelFor(selected[0])}`
        : `${label}: ${selected.length} selected`;

  return (
    <div className="eng-menu" ref={ref}>
      <button
        type="button"
        className={`eng-chip${active && !single ? " eng-chip--on" : ""}${open ? " eng-chip--open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {text}
        <i className="bi bi-chevron-down" />
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          className={`eng-menu__pop eng-menu__pop--fixed${placement.up ? " eng-menu__pop--up" : ""}`}
          style={{
            left: placement.left,
            top: placement.up ? undefined : placement.top,
            bottom: placement.up ? window.innerHeight - placement.top : undefined,
            minWidth: Math.max(placement.minWidth, 190),
          }}
        >
          {/* Only the options scroll. Clear is pinned outside that: inside a long list it
              scrolled out of sight, and the filter read as though it had no way to clear. */}
          <div className="eng-menu__options" style={{ maxHeight: placement.maxHeight }}>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className="eng-menu__item"
                data-checked={selected.includes(option.value) ? "true" : undefined}
                onClick={() => {
                  onToggle(option.value);
                  if (single) setOpen(false);
                }}
              >
                <i
                  className={`bi ${
                    single
                      ? selected.includes(option.value) ? "bi-record-circle" : "bi-circle"
                      : selected.includes(option.value) ? "bi-check-square" : "bi-square"
                  }`}
                />
                {option.label}
              </button>
            ))}
          </div>
          {active && !single && onClear && (
            <button type="button" className="eng-menu__clear" onClick={onClear}>
              Clear
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
